<template>
  <TableComp
    :headers="headers"
    :name="$t('global.cloud-servers')"
    :status="cloudServers.status"
    :pagination="cloudServers.pagination"
    :sort="cloudServers.sort"
    :number-of-items="cloudServers.list.length"
    v-on:update:list="updateList"
    :header-actions="tableActions"
    :empty-actions="getEmptyActions"
  >
    <template #desktop>
      <TableRow v-for="server in cloudServers.list" :key="server.id">
        <td class="align-middle">
          <router-link :to="{ name: 'cloud-server-detail', params: { id: server.id } }">
            {{ server.host }}
          </router-link>
          <br v-if="server.host" />
          <p>
            <span>
              {{ server.package.cpu }} {{ $t(`global.core${server.package.cpu === 1 ? "" : "s"}`) }}
            </span>
            | <span class="">{{ server.formattedDiskSpace }} {{ $t("global.storage") }}</span> |
            <span class=""
              >{{ server.package.formattedBandwidth }} {{ $t("global.bandwidth") }}</span
            >
            |
            <span class="">{{ server.package.formattedRam }} {{ $t("global.ram") }}</span>
          </p>
          {{ server.mainIp }}
          <CopyToClipboard :text="server.mainIp" />
        </td>
        <td class="align-middle text-center">
          <Btn @click="viewPassword(server.datashare)">
            {{ $t("global.view-password") }}
          </Btn>
        </td>
        <td class="align-middle text-center">
          <div class="d-flex justify-content-center">
            <Logo :logo="server.operatingSystem.distribution" :size="50" />
          </div>
          <div>
            <h4 class="text-capitalize">{{ server.operatingSystem.distribution }}</h4>
            <i>{{ server.operatingSystem.version }} [{{ server.operatingSystem.arch }}]</i>
          </div>
        </td>
        <td class="align-middle text-center">
          <div class="d-flex justify-content-center">
            <Logo :logo="`location-${server.location.country.toLowerCase()}`" :size="50" />
          </div>
          <div class="">
            <h4>{{ server.location.country }}</h4>
            <i>{{ server.location.name }}</i>
          </div>
        </td>
        <td class="align-middle text-center">
          <Pill
            :content="$t(`global.${server.status}`)"
            :variant="getStatusVariant(server.status)"
          />
        </td>
        <td class="align-middle text-end actions">
          <div v-if="server.isStable" class="d-flex justify-content-end mr-4">
            <ThreeDotsMenu :items="menuItems(server)" />
          </div>
          <div v-else class="d-flex justify-content-center align-items-center">
            <div class="mr-2">
              <LoadingState :size="20" />
            </div>
            <div class="text-capitalize">{{ server.state }}...</div>
          </div>
        </td>
      </TableRow>
    </template>
    <template #mobile>
      <TableMobileRow
        v-for="server in cloudServers.list"
        v-bind:key="server.id"
        :menu-items="menuItems(server)"
      >
        <template #header>
          <div class="d-flex align-items-center">
            <div class="flex-fill">
              <router-link :to="{ name: 'cloud-server-detail', params: { id: server.id } }">
                {{ server.host }}
              </router-link>
              <br v-if="server.host" />
              <p>
                <span>
                  {{ server.package.cpu }}
                  {{ $t(`global.core${server.package.cpu === 1 ? "" : "s"}`) }}
                </span>
                | <span class="">{{ server.formattedDiskSpace }} {{ $t("global.storage") }}</span> |
                <span class=""
                  >{{ server.package.formattedBandwidth }} {{ $t("global.bandwidth") }}</span
                >
                |
                <span class="">{{ server.package.formattedRam }} {{ $t("global.ram") }}</span>
              </p>
              {{ server.mainIp }}
              <CopyToClipboard :text="server.mainIp" />
            </div>
            <div>
              <div v-if="server.isStable" class="d-flex justify-content-center">
                <ThreeDotsMenu :items="menuItems(server)" />
              </div>
              <div v-else class="d-flex justify-content-center align-items-center">
                <div class="mr-2">
                  <loading-state :size="20" />
                </div>
                <div class="text-capitalize">{{ server.state }}...</div>
              </div>
            </div>
          </div>
        </template>

        <template #content>
          <div class="row">
            <div class="col">{{ $t("global.bandwidth") }}:</div>
            <div class="col">
              {{ server.bandwidth.used.billable }} / {{ server.bandwidth.value }}
              {{ server.bandwidth.type }}
            </div>
          </div>
          <div class="row">
            <div class="col">{{ $t("global.auto-renew") }}:</div>
            <div class="col">
              <Pill
                :content="$t(`global.${server.autoRenew}`)"
                :variant="getStatusVariant(server.autoRenew)"
              />
            </div>
          </div>
          <div class="row">
            <div class="col">{{ $t("global.next-bill") }}:</div>
            <div class="col">
              {{ server.billing.formattedNextBillDate }}
              <Tooltip
                v-if="server.paypal.hasSubscription"
                trigger="toggle"
                :content="
                  $t('global.active-paypal-tooltip', {
                    id: server.paypal.id,
                    startDate: server.paypal.formattedStartDate,
                  })
                "
              >
                <Icon icon="help" color="gamma" />@NEED PAYPAL ICON
              </Tooltip>
            </div>
          </div>
          <div class="row">
            <div class="col">{{ $t("global.price") }}:</div>
            <div class="col">
              {{ server.billing.price }}
            </div>
          </div>
          <div class="row">
            <div class="col">{{ $t("global.status") }}:</div>
            <div class="col">
              <Pill
                :content="$t(`global.${server.status}`)"
                :variant="getStatusVariant(server.status)"
              />
            </div>
          </div>
        </template>
      </TableMobileRow>
    </template>
  </TableComp>
</template>

<script>
import {
  getCdnControlPanelLogin,
  getConsole,
  getContactSupport,
  getDeleteCloudServer,
  getDeleteService,
  getHardReboot,
  getNewRelicLogin,
  getPurgeCache,
  getResize,
  getSoftReboot,
  getStart,
  getStop,
  getToggleEdgecastApac,
  getToggleEdgecastSharedSsl,
  getToggleNetworking,
  getUpdateReverseDns,
} from "@/utils/serviceMenuOptions";
import Btn from "@/components/display/Btn";
import CopyToClipboard from "@/components/display/CopyToClipboard";
import { getStatusVariant } from "@/utils/statusVariant";
import Icon from "@/components/display/icon/Icon";
import LoadingState from "@/components/display/LoadingState";
import Logo from "@/components/display/Logo";
import NavLink from "@/classes/NavLink";
import Pill from "@/components/display/Pill";
import store from "@/store";
import TableComp from "@/components/display/table/TableComp";
import TableHeader from "@/classes/TableHeader";
import TableMobileRow from "@/components/display/table/TableMobileRow";
import TableRow from "@/components/display/table/TableRow";
import ThreeDotsMenu from "@/components/display/ThreeDotsMenu";
import Tooltip from "@/components/display/Tooltip";
import ViewPassword from "@/components/smart/modals/ViewPassword";

export default {
  name: "CloudServersTable",
  props: {
    cloudServers: Object,
  },
  components: {
    Btn,
    CopyToClipboard,
    Icon,
    Logo,
    Pill,
    TableComp,
    TableMobileRow,
    TableRow,
    ThreeDotsMenu,
    Tooltip,
    LoadingState,
  },
  setup(props, { root }) {
    const headers = [
      new TableHeader(root.$t("global.details")),
      new TableHeader(),
      new TableHeader(root.$t("global.operating-system"), "justify-content-center"),
      new TableHeader(root.$t("global.location"), "justify-content-center"),
      new TableHeader(root.$t("global.status"), "justify-content-center"),
      new TableHeader(),
    ];

    const menuItems = (server) => {
      const items = [
        new NavLink(
          root.$t("global.view"),
          null,
          { name: "cloud-server-detail", params: { id: server.id } },
          false,
          "default"
        ),
      ];

      items.push(
        getCdnControlPanelLogin(server),
        getConsole(server),
        getContactSupport(server),
        getDeleteService(server),
        getHardReboot(server),
        getNewRelicLogin(server),
        getPurgeCache(server),
        getResize(server),
        getSoftReboot(server),
        getStart(server),
        getStop(server),
        getToggleEdgecastApac(server),
        getToggleEdgecastSharedSsl(server),
        getToggleNetworking(server),
        getUpdateReverseDns(server),
        getDeleteCloudServer(server)
      );

      return items.filter((i) => i !== null);
    };

    const viewPassword = (password) => {
      store.dispatch("modals/open", {
        template: ViewPassword,
        data: { password },
      });
    };

    const addCloudServer = () => root.$router.push({ name: "cloud-server-add" });

    const tableActions = [
      new NavLink(
        root.$t("global.add-cloud-server"),
        "add",
        addCloudServer,
        false,
        null,
        "epsilon"
      ),
    ];

    const getEmptyActions = [
      new NavLink(
        root.$t("global.add-cloud-server"),
        null,
        addCloudServer,
        false,
        "primaryOutline"
      ),
    ];

    const updateList = (event) => {
      const { pagination, sort } = event;
      store.dispatch("cloudServers/getList", {
        pageIndex: pagination?.index,
        pageSize: pagination?.size,
        sortProperty: sort?.property,
        sortDirection: sort?.direction,
      });
    };

    return {
      getStatusVariant,
      headers,
      menuItems,
      updateList,
      viewPassword,
      tableActions,
      getEmptyActions,
    };
  },
};
</script>
